import axios, { AxiosInstance } from 'axios';
import { 
  LoginRequest, 
  LoginResponse, 
  RefreshTokenRequest, 
  RefreshTokenResponse,
  User,
  Role,
  Permission,
  CreateUserRequest,
  UpdateUserRequest,
  CreateRoleRequest,
  UpdateRoleRequest,
  ChangePasswordRequest,
  ChangePasswordByAdminRequest
} from '../types/auth';
import { store } from '../store';
import { refreshAuthToken, forceLogout } from '../store/slices/authSlice';

// Flag to prevent multiple concurrent refresh attempts for auth service
let isAuthRefreshing = false;
let authFailedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

class AuthService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5026/api',
      timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000'),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('talabat_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        console.log(`[Auth API] ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => {
        console.log(`[Auth API] ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        console.error('[Auth API Error]', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.response?.data?.message || error.message,
        });        // Handle 401 Unauthorized responses for auth service
        if (error.response?.status === 401) {
          // Don't handle login or refresh requests to avoid infinite loops
          const isLoginRequest = error.config?.url?.includes('/auth/login');
          const isRefreshRequest = error.config?.url?.includes('/auth/refresh');
          
          if (!isLoginRequest && !isRefreshRequest) {
            const currentState = store.getState();
            const { refreshToken, isAuthenticated } = currentState.auth;
            
            if (isAuthenticated && refreshToken) {
              // If we're already refreshing, queue this request
              if (isAuthRefreshing) {
                return new Promise((resolve, reject) => {
                  authFailedQueue.push({ resolve, reject });
                }).then(() => {
                  // Retry the request with the new token
                  const newToken = store.getState().auth.token;
                  if (newToken && error.config) {
                    error.config.headers.Authorization = `Bearer ${newToken}`;
                    return axios.request(error.config);
                  }
                  return Promise.reject(error);
                });
              }
              
              isAuthRefreshing = true;
              
              // Attempt to refresh the token
              return store.dispatch(refreshAuthToken()).unwrap()
                .then(() => {
                  // Refresh succeeded - process queued requests
                  const newToken = store.getState().auth.token;
                  
                  // Retry the original request
                  if (newToken && error.config) {
                    error.config.headers.Authorization = `Bearer ${newToken}`;
                  }
                  
                  // Process all queued requests
                  authFailedQueue.forEach(({ resolve }) => resolve());
                  authFailedQueue = [];
                  isAuthRefreshing = false;
                  
                  if (newToken && error.config) {
                    return axios.request(error.config);
                  }
                  return Promise.reject(error);
                })
                .catch((refreshError) => {
                  // Refresh failed - reject all queued requests and logout
                  console.error('Token refresh failed in auth service:', refreshError);
                  authFailedQueue.forEach(({ reject }) => reject(refreshError));
                  authFailedQueue = [];
                  isAuthRefreshing = false;
                  
                  store.dispatch(forceLogout());
                  return Promise.reject(error);
                });
            } else {
              // No refresh token available, force logout
              store.dispatch(forceLogout());
            }
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Authentication endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  }

  async refreshToken(request: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const response = await this.api.post<RefreshTokenResponse>('/auth/refresh', request);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      // Don't throw on logout errors - we still want to clear local storage
      console.error('Logout error:', error);
    }
  }

  // User management endpoints
  async getUsers(): Promise<User[]> {
    const response = await this.api.get<User[]>('/users');
    return response.data;
  }

  async getUserById(id: string): Promise<User> {
    const response = await this.api.get<User>(`/users/${id}`);
    return response.data;
  }

  async createUser(request: CreateUserRequest): Promise<User> {
    const response = await this.api.post<User>('/users', request);
    return response.data;
  }

  async updateUser(id: string, request: UpdateUserRequest): Promise<User> {
    const response = await this.api.put<User>(`/users/${id}`, request);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  // Role management endpoints
  async getRoles(): Promise<Role[]> {
    const response = await this.api.get<Role[]>('/roles');
    return response.data;
  }

  async getRoleById(id: string): Promise<Role> {
    const response = await this.api.get<Role>(`/roles/${id}`);
    return response.data;
  }

  async createRole(request: CreateRoleRequest): Promise<Role> {
    const response = await this.api.post<Role>('/roles', request);
    return response.data;
  }

  async updateRole(id: string, request: UpdateRoleRequest): Promise<Role> {
    const response = await this.api.put<Role>(`/roles/${id}`, request);
    return response.data;
  }

  async deleteRole(id: string): Promise<void> {
    await this.api.delete(`/roles/${id}`);
  }

  // Permission endpoints
  async getPermissions(): Promise<Permission[]> {
    const response = await this.api.get<Permission[]>('/permissions');
    return response.data;
  }

  async getPermissionById(id: string): Promise<Permission> {
    const response = await this.api.get<Permission>(`/permissions/${id}`);
    return response.data;
  }
  // Password management endpoints
  async changePassword(request: ChangePasswordRequest): Promise<void> {
    await this.api.post('/auth/change-password', request);
  }
  async changePasswordByAdmin(request: ChangePasswordByAdminRequest): Promise<void> {
    await this.api.put('/auth/admin-change-password', request);
  }

  // Helper methods for token management
  setAuthToken(token: string): void {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearAuthToken(): void {
    delete this.api.defaults.headers.common['Authorization'];
  }

  // Check if user has specific permission
  hasPermission(user: User | null, permissionName: string): boolean {
    if (!user) return false;
    
    // SuperAdmin has all permissions
    if (user.permissions.some(p => p.name === '*:*')) {
      return true;
    }
    
    return user.permissions.some(p => p.name === permissionName);
  }

  // Check if user has specific role
  hasRole(user: User | null, roleName: string): boolean {
    if (!user) return false;
    return user.roles.some(r => r.name === roleName);
  }

  // Check if user can manage other users
  canManageUsers(user: User | null): boolean {
    return this.hasPermission(user, 'users:manage') || this.hasRole(user, 'Admin') || this.hasRole(user, 'SuperAdmin');
  }

  // Check if user can manage roles
  canManageRoles(user: User | null): boolean {
    return this.hasPermission(user, 'roles:manage') || this.hasRole(user, 'SuperAdmin');
  }

  // Check if user can access admin features
  isAdmin(user: User | null): boolean {
    return this.hasRole(user, 'Admin') || this.hasRole(user, 'SuperAdmin');
  }

  // Check if user is SuperAdmin
  isSuperAdmin(user: User | null): boolean {
    return this.hasRole(user, 'SuperAdmin');
  }
}

export const authService = new AuthService();
